import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from '../services/api';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || '';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => set({ token }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      setLoading: (isLoading) => set({ isLoading }),
      // Clear auth state locally without any network call (used when a refresh
      // fails) so we never re-enter the axios interceptor and loop.
      clearAuth: () => set({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }),

      // Login
      login: async (email, password) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await axios.post(`${API_URL}/api/auth/login`, {
            email,
            password
          }, {
            withCredentials: true
          });
          
          // Refresh token now lives only in an httpOnly cookie set by the server.
          const { token, user } = response.data;

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });

          toast.success(`Vítejte, ${user.firstName}!`);
          return user;
        } catch (error) {
          const message = error.response?.data?.error?.message || 
                         'Přihlášení se nezdařilo. Zkontrolujte prosím své údaje.';
          set({
            isLoading: false,
            error: message,
            user: null,
            token: null,
            isAuthenticated: false
          });
          toast.error(message);
          throw error;
        }
      },

      // Register
      register: async (formData) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await axios.post(`${API_URL}/api/auth/register`, formData);
          
          const { user, message } = response.data;
          
          // For registration, we don't automatically log in
          // User needs admin approval
          set({ isLoading: false });
          
          toast.success(message || 'Registrace úspěšná. Počkejte na schválení administrátorem.');
          return user;
        } catch (error) {
          const message = error.response?.data?.error?.message || 
                         'Registrace se nezdařila.';
          set({
            isLoading: false,
            error: message
          });
          toast.error(message);
          throw error;
        }
      },

      // Logout
      logout: async () => {
        try {
          set({ isLoading: true });
          
          await axios.post(`${API_URL}/api/auth/logout`, {}, {
            headers: {
              Authorization: `Bearer ${get().token}`
            },
            withCredentials: true
          });
        } catch (error) {
          // Ignore errors on logout
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          });
          toast.success('Odhlášeno úspěšně');
        }
      },

      // Check auth (on app load)
      checkAuth: async () => {
        try {
          const token = get().token;
          if (!token) {
            set({ isAuthenticated: false });
            return;
          }
          
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          const { user } = response.data;
          
          set({
            user,
            isAuthenticated: true,
            error: null
          });
        } catch (error) {
          console.error('Check auth error:', error);
          // Token might be expired, try to refresh via the httpOnly cookie
          if (error.response?.status === 401) {
            try {
              // refreshTokenFn sets user/token/isAuthenticated on success,
              // so there's no need to recurse into checkAuth (which could loop).
              await get().refreshTokenFn();
              return;
            } catch (refreshError) {
              console.error('Refresh token error:', refreshError);
              set({
                user: null,
                token: null,
                refreshToken: null,
                isAuthenticated: false,
                error: null
              });
              return;
            }
          }
          
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null
          });
        }
      },

      // Refresh token (refresh token is sent automatically via the httpOnly cookie)
      refreshTokenFn: async () => {
        try {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
            withCredentials: true
          });

          const { token, user } = response.data;

          set({
            token,
            user,
            isAuthenticated: true
          });

          return token;
        } catch (error) {
          const message = error.response?.data?.error?.message || 
                         'Refresh token selhal';
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            error: message
          });
          throw error;
        }
      },

      // Change password
      changePassword: async (currentPassword, newPassword) => {
        try {
          set({ isLoading: true, error: null });
          
          const token = get().token;
          await axios.post(`${API_URL}/api/auth/change-password`, {
            currentPassword,
            newPassword
          }, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          set({ isLoading: false });
          toast.success('Heslo úspěšně změněno');
        } catch (error) {
          const message = error.response?.data?.error?.message || 
                         'Změna hesla se nezdařila';
          set({
            isLoading: false,
            error: message
          });
          toast.error(message);
          throw error;
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

// Selector for easy access
export const selectUser = (state) => state.user;
export const selectToken = (state) => state.token;
export const selectIsAuthenticated = (state) => state.isAuthenticated;
export const selectIsAdmin = (state) => state.user?.role === 'ADMIN';
export const selectIsPending = (state) => state.user?.role === 'PENDING';
